import React from 'react';
import useInView from '../../hooks/useInView';
import { Box, Typography, Button } from '@mui/material';
import { FONT_FAMILY } from '../../theme';

export default function InfoSection({
    title = 'Come on in and get\nyour hookah!',
    subtitle = 'Nearly 100 flavors, premium hookah setups and a cozy atmosphere — we craft your perfect evening.',
    ctaText = 'View Menu',
}) {
    const [ref, inView] = useInView({ threshold: 0.15 });

    return (
        <Box ref={ref} sx={{ width: '100%', minHeight: { xs: 420, md: 560 }, mb: 0, display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, alignItems: 'center', justifyContent: 'center', bgcolor: '#fff', overflow: 'visible', position: 'relative', borderRadius: 0, boxShadow: 3, pl: 0, pr: 0, mx: 0, py: { xs: 4, md: 6 } }}>
            {/* Left: Image collage */}
            <Box sx={{ width: { xs: '100%', md: '50%' }, display: 'flex', gap: 2, px: { xs: 3, md: 6 }, justifyContent: 'center', alignItems: 'center' }}>
                <Box sx={{ width: { xs: '60%', md: '55%' }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{
                        height: { xs: 160, md: 260 }, overflow: 'hidden', borderRadius: 3,
                        transition: 'transform 420ms cubic-bezier(.2,.9,.2,1), box-shadow 420ms',
                        '&:hover img': { transform: 'translateY(-8px) scale(1.03)', boxShadow: '0 26px 60px rgba(0,0,0,0.28)' }
                    }}>
                        <img src={process.env.PUBLIC_URL + '/images/gallery-images/20231001_195210-scaled.jpg'} alt="Feature 1" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 420ms cubic-bezier(.2,.9,.2,1), box-shadow 420ms' }} />
                    </Box>
                    <Box sx={{
                        height: { xs: 120, md: 180 }, overflow: 'hidden', borderRadius: 3,
                        transition: 'transform 420ms cubic-bezier(.2,.9,.2,1), box-shadow 420ms',
                        '&:hover img': { transform: 'translateY(-6px) scale(1.02)', boxShadow: '0 18px 44px rgba(0,0,0,0.22)' }
                    }}>
                        <img src={process.env.PUBLIC_URL + '/images/gallery-images/20231204213504_IMG_0262-scaled.jpg'} alt="Feature 2" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 420ms cubic-bezier(.2,.9,.2,1), box-shadow 420ms' }} />
                    </Box>
                </Box>
                <Box sx={{ width: { xs: '35%', md: '40%' }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{
                        height: { xs: 200, md: 440 }, overflow: 'hidden', borderRadius: 3,
                        transition: 'transform 420ms cubic-bezier(.2,.9,.2,1), box-shadow 420ms',
                        '&:hover img': { transform: 'translateY(-10px) scale(1.035)', boxShadow: '0 36px 80px rgba(0,0,0,0.32)' }
                    }}>
                        <img src={process.env.PUBLIC_URL + '/images/gallery-images/20231021_200422-scaled.jpg'} alt="Feature 3" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 420ms cubic-bezier(.2,.9,.2,1), box-shadow 420ms' }} />
                    </Box>
                </Box>
            </Box>

            {/* Right: Text content + decorative shapes */}
            <Box sx={{ width: { xs: '100%', md: '50%' }, px: { xs: 3, md: 10 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2, position: 'relative' }}>
                <Box sx={{ position: 'absolute', left: -40, top: -20, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(237,80,52,0.06)', filter: 'blur(18px)', display: { xs: 'none', md: 'block' }, zIndex: 1 }} />
                <Typography
                    variant="h2"
                    sx={{
                        fontWeight: 800,
                        fontSize: { xs: '1.3rem', sm: '1.6rem', md: '2.2rem', lg: '2.6rem' },
                        mb: 1,
                        color: '#111',
                        fontFamily: FONT_FAMILY,
                        lineHeight: 1.05,
                        letterSpacing: 0.6,
                        textAlign: { xs: 'center', md: 'left' },
                        zIndex: 3,
                    }}
                    style={{
                        animation: inView ? 'fadeInUp 0.8s ease both' : 'none',
                        opacity: inView ? 1 : 0,
                        transform: inView ? 'translateY(0)' : 'translateY(18px)',
                        transition: 'opacity 700ms ease, transform 700ms ease'
                    }}
                >
                    {title.split('\n').map((line, i) => (<span key={i} style={{ display: 'block' }}>{line}</span>))}
                </Typography>
                <Box sx={{
                    width: 90,
                    height: 6,
                    bgcolor: '#ed5034',
                    borderRadius: 2,
                    mb: 2,
                    mx: { xs: 'auto', md: 0 }
                }} />
                <Typography
                    variant="body1"
                    sx={{
                        fontSize: 18,
                        color: '#666',
                        fontFamily: 'Montserrat, sans-serif',
                        mb: 2,
                        zIndex: 3,
                        textAlign: { xs: 'center', md: 'left' },
                    }}
                    style={{
                        animation: inView ? 'fadeInUp 1s ease both' : 'none',
                        opacity: inView ? 1 : 0,
                        transform: inView ? 'translateY(0)' : 'translateY(18px)',
                        transition: 'opacity 800ms ease 100ms, transform 800ms ease 100ms'
                    }}
                >
                    {subtitle}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, mt: { xs: 6, md: 3 }, mb: { xs: 5, md: 0 }, zIndex: 3, justifyContent: { xs: 'center', md: 'flex-start' } }}
                    style={{
                        opacity: inView ? 1 : 0,
                        transform: inView ? 'translateY(0)' : 'translateY(18px)',
                        transition: 'opacity 700ms ease 200ms, transform 700ms ease 200ms'
                    }}
                >
                    <Button href="/menu" variant="contained" sx={{ bgcolor: '#ed5034', '&:hover': { bgcolor: '#d64528' }, px: 4, py: 1.1 }}>{ctaText}</Button>
                </Box>

                <style>{`
                    @keyframes fadeInUp {
                        0% { opacity: 0; transform: translateY(18px); }
                        100% { opacity: 1; transform: translateY(0); }
                    }
                    .info-img:hover img { transform: scale(1.04); }
                `}</style>
            </Box>
        </Box>
    );
}
